import {
  Palette as DesignIcon,
  Folder as FolderIcon,
  Home as HomeIcon,
  Login as LoginIcon,
  Menu as MenuIcon,
  PhotoLibrary as PhotoIcon,
  BugReport as TestIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import {
  AppBar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const navigationItems = [
    { path: '/', label: 'Home', icon: <HomeIcon /> },
    { path: '/files', label: 'Files', icon: <FolderIcon /> },
    { path: '/upload', label: 'Upload', icon: <UploadIcon /> },
    { path: '/design', label: 'Design', icon: <DesignIcon /> },
    { path: '/login', label: 'Login', icon: <LoginIcon /> },
    { path: '/test', label: 'Test', icon: <TestIcon /> },
  ];

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  const isActivePage = (path: string) => {
    return location.pathname === path;
  };

  const renderDesktopNavigation = () => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {navigationItems.map((item) => (
        <Button
          key={item.path}
          color="inherit"
          startIcon={item.icon}
          onClick={() => handleNavigation(item.path)}
          sx={{
            backgroundColor: isActivePage(item.path) ? 'rgba(255,255,255,0.1)' : 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)',
            },
            borderRadius: 2,
            px: 2,
            py: 1,
          }}
        >
          {item.label}
        </Button>
      ))}
    </Box>
  );

  const renderMobileNavigation = () => (
    <>
      <IconButton
        color="inherit"
        aria-label="menu"
        onClick={handleMenuOpen}
        sx={{ ml: 'auto' }}
      >
        <MenuIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {navigationItems.map((item) => (
          <MenuItem
            key={item.path}
            onClick={() => handleNavigation(item.path)}
            selected={isActivePage(item.path)}
            sx={{ gap: 2 }}
          >
            {item.icon}
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );

  return (
    <AppBar position="static" elevation={2}>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PhotoIcon sx={{ fontSize: 32 }} />
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 600,
              fontSize: '1.5rem',
              background: 'linear-gradient(45deg, #ffffff, #e8f5e8)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Family Archive
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {isMobile ? renderMobileNavigation() : renderDesktopNavigation()}

        <Box sx={{ ml: 2 }}>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            v2.0
          </Typography>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navigation;
